import React from "react";
import { Typography } from "antd";

// ══════════════════════════════════════════════════════════════
// XP DE EVENTOS Y NOTICIAS (agregado 06/10/2026)
//
// Sección opcional que indica cuánta XP se puede ganar participando
// de un evento o de lo que propone una noticia. Se carga desde el
// panel de Admin (campos Exp / ExpDetalle en Noticias, exp / expDetalle
// en Eventos).
//
// Si la XP es 0, vacía o inválida, el componente no muestra nada:
// así un evento o noticia que no suma XP se ve igual que antes.
//
// Es solo informativo: NO suma XP al usuario. La XP se sigue
// acreditando desde el panel de Admin (o el importador).
// ══════════════════════════════════════════════════════════════

/** Convierte lo que venga de Firestore en una XP válida (entero >= 0). */
export const parseXpReward = (value: unknown): number => {
  const num =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim()
        ? Number(value)
        : 0;
  return Number.isFinite(num) && num > 0 ? Math.round(num) : 0;
};

interface XpRewardSectionProps {
  exp?: number | null;
  /** Texto opcional: qué hay que hacer para ganarla. */
  detail?: string;
  /** Versión más chica, para el carrusel de noticias. */
  compact?: boolean;
}

const XpRewardSection: React.FC<XpRewardSectionProps> = ({
  exp,
  detail,
  compact = false,
}) => {
  const xp = parseXpReward(exp);
  if (!xp) return null;

  const cleanDetail = (detail ?? "").trim();

  return (
    <div
      role="note"
      aria-label={`Podés ganar ${xp} XP`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: compact ? 10 : 12,
        padding: compact ? "8px 12px" : "12px 14px",
        borderRadius: 14,
        background: "rgba(124, 92, 252, 0.08)",
        border: "1px solid rgba(124, 92, 252, 0.28)",
      }}
    >
      <span
        aria-hidden
        style={{
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: compact ? "3px 10px" : "5px 12px",
          borderRadius: 999,
          background: "linear-gradient(135deg, #7c5cfc 0%, #9b7dfc 100%)",
          color: "#fff",
          fontWeight: 800,
          fontSize: compact ? 13 : 15,
          whiteSpace: "nowrap",
        }}
      >
        ⚡ +{xp} XP
      </span>

      <div style={{ minWidth: 0 }}>
        <Typography.Text
          strong
          style={{
            display: "block",
            color: "var(--dashboard-header-blue)",
            fontSize: compact ? 13 : 14,
          }}
        >
          Podés ganar {xp} XP
        </Typography.Text>
        {cleanDetail ? (
          <Typography.Text
            style={{
              display: "block",
              color: "var(--dashboard-text)",
              fontSize: compact ? 12 : 13,
              lineHeight: 1.5,
              whiteSpace: "pre-line",
            }}
          >
            {cleanDetail}
          </Typography.Text>
        ) : null}
      </div>
    </div>
  );
};

export default XpRewardSection;
